import {
  buildDateFilter,
  buildSingleValue,
  buildStructuredFilter,
  convertFilterOperatorToStructuredFilterOperator,
  convertStructuredFilterOperatorToFilterOperator,
  convertStructuredFilterToFilterSetQuery,
  parseDateFilter,
  STRUCTURED_FILTER_OPERATOR,
  validateEmptyFilter,
} from '@/entities/(filter)/common';
import {
  COLUMN_FILTER_STATUS_KEY_MAP,
  type FEEDBACK_COLUMNS,
  FeedbackColumnNameMap,
  READONLY_COLUMN_KEY,
  SOURCE_COLUMNS,
} from '@/types/filterKeyConst';
import {
  structuredFilterOperatorDic,
} from '@/types/structuredFilterConst';
import filterUtils from '@/utils/filter';

type GetOperatorOptions = {
  operator?: STRUCTURED_FILTER_OPERATOR;
  // 현재 선택된 값이 여러개인 경우
  hasMultiValues?: boolean;
  // 필드가 여러개 값을 가질 수 있는 경우
  isMultiValueField?: boolean;
};

const getOperatorTextMap = (options?: GetOperatorOptions): string => {
  if (options?.operator === STRUCTURED_FILTER_OPERATOR.NOR) {
    if (options?.isMultiValueField) {
      if (options?.hasMultiValues) {
        if (options?.operator) {
          return structuredFilterOperatorDic[options?.operator];
        } else {
          throw new Error('When multifilter is true, operator must be given');
        }
      } else {
        return 'does not include';
      }
    } else {
      if (options?.hasMultiValues) {
        return 'is not';
      } else {
        return 'is not';
      }
    }
  } else {
    if (options?.isMultiValueField) {
      if (options?.hasMultiValues) {
        if (options?.operator) {
          return structuredFilterOperatorDic[options?.operator];
        } else {
          throw new Error('When multifilter is true, operator must be given');
        }
      } else {
        return 'include';
      }
    } else {
      if (options?.hasMultiValues) {
        return 'is any of';
      } else {
        return 'is';
      }
    }
  }
};

const getStructuredFilterStatusQuery = (
  filterQueryFormats: StructuredFilterQueryFormatT[],
  disableReadOnly?: boolean,
): QueryT[] => {
  let result: QueryT[] = [];
  filterQueryFormats.forEach((filterQueryFormat) => {
    const columnKey =
      COLUMN_FILTER_STATUS_KEY_MAP[filterQueryFormat.columnKey] ||
      filterQueryFormat.columnKey;
    if (columnKey === SOURCE_COLUMNS.SOURCE_TYPE) {
      // SOURCE의 경우 source, source_domain, source_ch 로 query분할
      const sourceQueryArr = filterUtils.convertSourceKeyToFilterQueryArray(
        filterQueryFormat.filterValues
          .map((value) => value.filterKey)
          .join(
            convertStructuredFilterOperatorToFilterOperator(
              filterQueryFormat.operator,
            ),
          ),
      );
      result.push(...sourceQueryArr);
    } else {
      const value = filterQueryFormat.filterValues
        .map((value) => value.filterKey)
        .join(
          convertStructuredFilterOperatorToFilterOperator(
            filterQueryFormat.operator,
          ),
        );
      result.push({
        key: columnKey,
        value,
      });
    }
    const readonlyColumnKey = READONLY_COLUMN_KEY[columnKey];
    if (!disableReadOnly && readonlyColumnKey) {
      const filterName = filterQueryFormat.filterValues
        .map((value) => value.filterName)
        .join(
          convertStructuredFilterOperatorToFilterOperator(
            filterQueryFormat.operator,
          ),
        );
      const targetIndex = result.findIndex(
        (query) => query.key === readonlyColumnKey,
      );
      if (targetIndex === -1) {
        result.push({
          key: readonlyColumnKey,
          value: filterName,
        });
      } else {
        result.splice(targetIndex, 1, {
          key: readonlyColumnKey,
          value: filterName,
        });
      }
    }
  });
  return result;
};

const getFieldInfo = (
  columnKey: string,
  fieldOptions: StructuredFieldOptionMap,
): {
  fieldInfo: StructuredField;
  parentFieldName?: string;
} | null => {
  const targetField = fieldOptions[columnKey];
  if (!targetField) {
    for (const field of Object.values(fieldOptions)) {
      if (field.childField) {
        for (const child of Object.values(field.childField)) {
          if (child.columnKey === columnKey) {
            return {
              fieldInfo: child,
              parentFieldName: field.columnName,
            };
          }
        }
      }
    }

    console.error(`Field not found: ${columnKey}`);
    return null;
  }
  return {
    fieldInfo: targetField,
  };
};

const getStaticDataFilterString = (
  filter: StructuredFilterQueryFormatT[],
): string | null => {
  if (filter.length === 0) return null;
  const filterString = filter.reduce((acc, value) => {
    const columnName = FeedbackColumnNameMap.get(
      value.columnKey as FEEDBACK_COLUMNS,
    );
    if (!columnName) return acc;
    const operatorText = getOperatorTextMap({
      operator: value.operator,
      isMultiValueField: true,
      hasMultiValues: value.filterValues.length > 1,
    });

    acc.push(
      `${columnName} ${operatorText} ${value.filterValues
        .map((v) => v.filterName)
        .join(', ')}`,
    );

    return acc;
  }, [] as string[]);
  return filterString.join(' / ');
};

const getStaticKeywordFilterString = (
  valueSets: StructuredFilterStringNumberValueSet,
): {
  operatorString: string;
  valueString: string;
} | null => {
  if (valueSets.values.length === 0) return null;
  const operatorString = getOperatorTextMap({
    operator: valueSets.op,
    isMultiValueField: true,
    hasMultiValues: valueSets.values.length > 1,
  });
  const valueString = valueSets.values.join(', ');
  return {
    operatorString,
    valueString,
  };
};

const structuredFilterUtils = {
  convertFilterOperatorToStructuredFilterOperator,
  convertStructuredFilterToFilterSetQuery,
  buildSingleValue,
  getOperatorTextMap,
  buildStructuredFilter,
  getStructuredFilterStatusQuery,
  buildDateFilter,
  parseDateFilter,
  getFieldInfo,
  validateEmptyFilter,
  getStaticDataFilterString,
  getStaticKeywordFilterString,
};

export default structuredFilterUtils;
